/**
 * Custom fetch mutator used by the Orval-generated client.
 * Same-origin requests: the dev preview and production server both serve
 * the API under /api, so no base URL is required.
 *
 * When authentication is enabled the frontend registers a token provider
 * (see setAuthTokenProvider); the mutator then attaches a bearer token to
 * every request. Tokens stay in memory — never persisted here.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export type AuthTokenProvider = () => Promise<string | null>;

let tokenProvider: AuthTokenProvider | null = null;

/** Register (or clear) the function used to obtain bearer tokens. */
export function setAuthTokenProvider(provider: AuthTokenProvider | null): void {
  tokenProvider = provider;
}

/** Resolve the current bearer token, or null when auth is disabled. */
export async function getAuthToken(): Promise<string | null> {
  if (!tokenProvider) return null;
  try {
    return await tokenProvider();
  } catch {
    return null;
  }
}

async function buildHeaders(options: RequestInit): Promise<HeadersInit> {
  const isJsonBody = typeof options.body === 'string';
  const token = await getAuthToken();
  return {
    Accept: 'application/json',
    ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers
  };
}

export const customFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  // Strip the AbortSignal to prevent AbortError noise from React Strict
  // Mode's unmount/remount cycle. In-flight requests complete and cache
  // their results for the remounted component instead of being aborted.
  const fetchOptions: RequestInit = { ...options };
  delete fetchOptions.signal;

  const response = await fetch(url, {
    ...fetchOptions,
    headers: await buildHeaders(fetchOptions)
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const body = isJson
      ? ((await response.json().catch(() => undefined)) as
          | { error?: string; code?: string }
          | undefined)
      : undefined;
    throw new ApiError(
      body?.error ?? `Request failed with status ${response.status}`,
      response.status,
      body?.code
    );
  }

  if (!isJson) {
    // Binary endpoints (file download) resolve to a Blob.
    return (await response.blob()) as T;
  }

  return (await response.json()) as T;
};
